import { JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button } from '../../components/button';
import { ConfirmAction } from '../../components/confirm-action';
import { Error } from '../../components/error';
import { Form } from '../../components/form';
import { Habit } from '../../dto/habit';
import {
  HabitTargetType,
  TargetDirection,
  TargetDirectionLabels,
  TargetEvaluation,
  TargetEvaluationLabels,
} from '../../dto/habit.enum';
import { useFirebase } from '../../hooks/firebase.hook';
import { useHabit } from '../../hooks/habit.hook';
import { useLayout } from '../../hooks/layout.hook';
import { useNavigation } from '../../hooks/navigation.hook';

enum UpdateProcess {
  END = 'End',
  RESET = 'Reset',
  DELETE = 'Delete',
}

interface FormData {
  title: string;
  notes?: string;
  target?: string;
  evaluation?: TargetEvaluation;
  direction?: TargetDirection;
}

export function HabitEditScreen(): JSX.Element {
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const location = useLocation();
  const { editHabit, endHabit, resetHabit, deleteHabit } = useHabit();
  const { translateError } = useFirebase();

  const habit = location.state as Habit;

  const [isUpdating, setIsUpdating] = useState(false);
  const [process, setProcess] = useState<UpdateProcess>();
  const [error, setError] = useState<string>();
  const [targetEvaluation, setTargetEvaluation] = useState<TargetEvaluation>(habit.target.evaluation);
  const [targetDirection, setTargetDirection] = useState<TargetDirection>(habit.target.direction);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!habit) navigate(-1);
  }, [habit]);

  function handleSubmit({ title, notes, target, evaluation, direction }: FormData) {
    setIsUpdating(true);

    editHabit(habit, {
      title,
      notes,
      target:
        target && evaluation && direction ? { ...habit.target, value: +target, evaluation, direction } : undefined,
    })
      .then(() => navigate(-1))
      .catch((e) => setError(translateError(e.message)))
      .finally(() => setIsUpdating(false));
  }

  function onProcess() {
    let task;
    switch (process) {
      case UpdateProcess.END:
        task = endHabit(habit);
        break;

      case UpdateProcess.RESET:
        task = resetHabit(habit);
        break;

      case UpdateProcess.DELETE:
        task = deleteHabit(habit);
        break;
    }

    setIsUpdating(true);
    task
      ?.then(() => navigate(-1))
      .catch((e) => setError(translateError(e.message)))
      .finally(() => setIsUpdating(false));
  }

  const processMessages = {
    [UpdateProcess.END]:
      'This locks the habit for further editing and removes it from the task list for future days. However, the data is preserved, and the statistics remain visible.',
    [UpdateProcess.RESET]:
      'This deletes all historical entries associated with this habit, but the habit itself is preserved, so you can start over. This operation is irreversible!',
    [UpdateProcess.DELETE]:
      'This deletes the habit and all associated statistical data. This operation is irreversible!',
  };

  useLayout(
    () => ({
      activeMenu: 'habits',
      title: process ? t(`${process} Habit`) : t('Edit Habit'),
      titleSize: 'text-2xl',
      backButton: true,
      onBack: process ? () => setProcess(undefined) : undefined,
    }),
    [t, process],
  );

  return process != null ? (
    <ConfirmAction
      title={t('Are you sure you want to {{action}} your habit "{{habit}}"?', {
        action: t(process).toLowerCase(),
        habit: t(habit.title),
      })}
      message={t(processMessages[process])}
      onConfirm={onProcess}
      onCancel={() => setProcess(undefined)}
      loading={isUpdating}
    />
  ) : (
    <>
      <Form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-3 mt-2">
          <div>
            <div className="text-base-content/70 mb-1">{t('Label')}</div>
            <input
              defaultValue={t(habit.title)}
              name="title"
              className="input input-lg validator w-full"
              placeholder={t('Label')}
              required
            />
            <div className="validator-hint hidden">{t('Required')}</div>
          </div>

          <div>
            <div className="text-base-content/70 mb-1">{t('Personal goal')}</div>
            <textarea
              defaultValue={habit.notes}
              className="textarea textarea-lg w-full"
              placeholder={t('Personal goal')}
              name="notes"
            />
          </div>

          {habit.target.type === HabitTargetType.QUANTITY && (
            <>
              <div>
                <div className="text-base-content/70 mb-1">{t('Target')}</div>
                <input
                  defaultValue={habit.target.value}
                  type="number"
                  name="target"
                  className="input input-lg validator w-full"
                  placeholder={t('Target')}
                  required
                />
                <div className="validator-hint hidden">{t('Required')}</div>
              </div>

              <div>
                <div className="text-base-content/70 mb-1">{t('What kind of target is it?')}</div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <select
                    className="select select-lg w-full"
                    name="evaluation"
                    value={targetEvaluation}
                    onChange={(e) => setTargetEvaluation(e.target.value as TargetEvaluation)}
                  >
                    {Object.values(TargetEvaluation).map((te) => (
                      <option key={te} value={te}>
                        {t(TargetEvaluationLabels[te])}
                      </option>
                    ))}
                  </select>
                  <select
                    className="select select-lg w-full"
                    name="direction"
                    value={targetDirection}
                    onChange={(e) => setTargetDirection(e.target.value as TargetDirection)}
                  >
                    {Object.values(TargetDirection).map((td) => (
                      <option key={td} value={td}>
                        {t(TargetDirectionLabels[td])}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          <Button className="btn btn-primary btn-lg mt-4" type="submit" loading={isUpdating}>
            {t('Update')}
          </Button>

          {error && <Error msg={error} />}
        </div>
      </Form>

      <div className="divider" />

      <div className="flex flex-col gap-3">
        <button className="btn btn-lg btn-neutral" type="button" onClick={() => setProcess(UpdateProcess.END)}>
          {t('End habit')}
        </button>

        <button className="btn btn-lg btn-neutral" type="button" onClick={() => setProcess(UpdateProcess.RESET)}>
          {t('Reset habit')}
        </button>

        <button className="btn btn-lg btn-neutral" type="button" onClick={() => setProcess(UpdateProcess.DELETE)}>
          {t('Delete habit')}
        </button>
      </div>
    </>
  );
}
